import { AI_USER_ACTION } from '@/lib/api/ai-user/ai-user-api'
import type { PaymentQuoteOption } from '@/lib/api/financial/financial-billing-api'
import { getSaleSimulatorOptions } from '@/lib/api/financial/sale-simulator-api'

/**
 * Simulação de pagamento do usuário de IA (ver docs/ai-user.md, "Simular o pagamento").
 *
 * É o cálculo do "Simulador de vendas", pela mesma rota e com a mesma permissão
 * (`financial.sales_simulator`), mas sem abrir a tela: sair do agendamento perderia os
 * itens montados. O resultado vai resumido ao modelo; cada campo custa tokens.
 */

export function isPaymentSimulationAction(name: string): boolean {
    return name === AI_USER_ACTION.simulatePayment
}

const formatCents = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/** O que o modelo recebe: total por forma e, no crédito, cada parcela. Valores em centavos. */
export function summarizePaymentOptions(amount: number, options: PaymentQuoteOption[]) {
    const single = (method: PaymentQuoteOption['method']) => {
        const option = options.find(entry => entry.method === method && entry.installments === 1)
        return option ? { total: option.total } : undefined
    }
    const credit = options
        .filter(entry => entry.method === 'CREDIT_CARD')
        .sort((a, b) => a.installments - b.installments)
        .map(entry => ({
            installments: entry.installments,
            installmentAmount: entry.installmentAmount,
            total: entry.total,
            // Sem acréscimo: o contato paga o valor da venda, só dividido.
            interestFree: entry.total <= amount,
        }))
    const interestFree = credit.filter(entry => entry.interestFree)
    return {
        amount,
        pix: single('PIX'),
        debitCard: single('DEBIT_CARD'),
        creditCard: credit.length
            ? {
                  interestFreeUpTo: interestFree.length ? interestFree[interestFree.length - 1].installments : 0,
                  options: credit,
              }
            : undefined,
    }
}

export async function executePaymentSimulation(args: Record<string, unknown>): Promise<string> {
    const amount = Math.round(Number(args.amount))
    if (!Number.isFinite(amount) || amount <= 0) {
        return JSON.stringify({ ok: false, messages: ['Erro: informe o valor em centavos, maior que zero.'] })
    }
    const response = await getSaleSimulatorOptions(amount)
    if (!response.success || !response.result) {
        return JSON.stringify({
            ok: false,
            messages: [`Erro: a simulação falhou (${response.error || 'sem resposta'}). Use ask_supervisor se precisar do valor.`],
        })
    }
    return JSON.stringify({ ok: true, ...summarizePaymentOptions(amount, response.result.options) })
}

/** Uma linha para o Diário. */
export function describePaymentSimulation(args: Record<string, unknown>): string {
    const amount = Number(args.amount)
    return Number.isFinite(amount) && amount > 0
        ? `Simulando o pagamento de ${formatCents(amount)}.`
        : 'Simulando o pagamento.'
}
